import type { HomeNumbers as Numbers } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { api, messageOf } from '../api-client.js';

/**
 * 老板看板首屏五数（ADR-0047）。
 *
 * ## 这一版只有三格有数
 *
 * | # | 数 | 状态 |
 * |---|---|---|
 * | 1 岗位 | 在役岗位数 | 有 |
 * | 2 任务卡（已建模·规划） | 资产规模，数卡不数执行 | 有 |
 * | 3 今天在运行 | 通量 | 有 |
 * | 4 AI 替代率 | 分母 = 岗位编制工时 | **无源** |
 * | 5 日总工时·AI 完成 | 同上 | **无源** |
 *
 * 第 2 与第 3 **不可互相替代**（ADR-0047 三，依据规范 4.1「资产规模只数卡，不数执行」）：
 * 前者是「有多少资产」，后者是「今天用了多少」。合成一个数，
 * 「资产增加了」与「用得多了」就再也分不开。
 *
 * ## 0 与「算不出来」在视觉上必须不同（C-099R 三）
 *
 * 这是本组件最要紧的一条：
 *
 * - **「今天在运行 0」是真值**——今天确实一次都没跑，这个 0 有信息量，
 *   所以它就正常显示成一个大字 `0`
 * - **「AI 替代率」无源是另一回事**——它不是「AI 一点没干」。
 *   所以它**不显示数字**：位置上是一条灰的「待编制数据到位」，加一句缺什么
 *
 * 若两者长得一样，看板上「替代率 0%」会被读成「AI 完全没起作用」，
 * 而实际含义是「这个数还没有源」。**那是一个会误导决策的假值**（P-13、P-15）。
 */
export function HomeNumbers(): ReactNode {
  const [numbers, setNumbers] = useState<Numbers | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reload(): void {
    void api.homeNumbers().then(
      (data) => {
        setNumbers(data);
        setError(null);
      },
      (cause: unknown) => setError(messageOf(cause)),
    );
  }

  useEffect(reload, []);

  if (error !== null) return <p className="msg">{error}</p>;
  if (numbers === null) return <p className="sub-hint">读取中……</p>;

  return (
    <section className="home-numbers" aria-label="首屏五数">
      {/*
        **一格两数**：岗位数为主（大字），在岗人数为辅（小字在下）。
        不新增第六格——两个数回答的是同一个问题的两面：
        「有多少个岗位」与「这些岗位上现在有多少人」。

        两个都是**统计值**：岗位数来自岗位表，人数来自人员表的在岗区间。
        没有任何界面能直接填它们。企业级与部门级同法。
      */}
      <div className="hn-cell">
        <div className="hn-label">岗位</div>
        <div className="hn-value">
          {numbers.positions}
          <span className="hn-unit"> 个</span>
        </div>
        <div className="hn-sub">在岗 {numbers.staffedPeople} 人</div>
      </div>

      {/*
        一格两态。分开显示而不是加总——proposed 还不是资产（ADR-0042、F 段六），
        并进去会让「有人提了个想法」和「建成了一张卡」变成同一个数。
      */}
      <div className="hn-cell">
        <div className="hn-label">任务卡</div>
        <div className="hn-value">
          {numbers.cards.modeled}
          <span className="hn-unit"> 已建模</span>
        </div>
        <div className="hn-sub">
          {numbers.cards.planned} 规划
        </div>
      </div>

      <Cell label="今天在运行" value={numbers.runningToday} unit="次" />

      <Blocked label="AI 替代率" state={numbers.aiSubstitutionRate} />
      <Blocked label="日总工时 · AI 完成" state={numbers.aiCompletedHours} />
    </section>
  );
}

function Cell({
  label,
  value,
  unit,
}: {
  label: string;
  value: number;
  unit: string;
}): ReactNode {
  return (
    <div className="hn-cell">
      <div className="hn-label">{label}</div>
      <div className="hn-value">
        {value}
        <span className="hn-unit"> {unit}</span>
      </div>
    </div>
  );
}

/**
 * 一格可能算不出来的数。
 *
 * **算不出来时不渲染数字位。** 不是显示「0」、不是显示「--」占位——
 * 那两种都会被眼睛读成「有这个数，值是零／很小」。
 * 这里换成一条说明，并把「缺什么」写出来：
 * 「暂无数据」会让人以为再等等就有了，而它其实在等一个要人去做的动作。
 */
function Blocked({
  label,
  state,
}: {
  label: string;
  state: { value: number } | { value: null; blockedBy: string };
}): ReactNode {
  if (state.value !== null) {
    return <Cell label={label} value={state.value} unit="" />;
  }
  return (
    <div className="hn-cell hn-blocked">
      <div className="hn-label">{label}</div>
      <div className="hn-pending">待编制数据到位</div>
      <div className="hn-sub">{state.blockedBy}</div>
    </div>
  );
}
